import { isEmpty, isPresent } from '@ember/utils';
import { isTruthy } from '@linc-technologies/linced-common/utils/strings';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import type {
  FieldOption,
  FieldOptionSource,
} from '@linc-technologies/linced-common/utils/fields';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';

// The valueList of a dynamic-field/value. The LIST case iterates it.
type ListValue = Value['valueList'];

// A Date matches a date filter in any common format, whatever the dataType.
export type FilterableValue =
  string | number | boolean | Date | ListValue | null | undefined;

// The options that admin/list sends. Schema fields send FieldOption.
// Enum fields send EnumEntry.
// conditionTypeOptions sends `{ valueInt, label }` that it makes from an enum.
// STRING and LIST filters match `value`. INTEGER filters match `valueInt`.
type FilterOption = FieldOptionSource | Pick<FieldOption, 'valueInt' | 'label'>;

const numericOperators = /^[<>]=?/;

function filterNumeric(filter: string, value: unknown): boolean {
  if (numericOperators.test(filter)) {
    const remainder = filter.replace(numericOperators, '').trim();
    const filterValue = Number(remainder);
    if (isNaN(filterValue)) {
      // No valid number to check against - match everything if no value yet, nothing if there's an invalid value
      return remainder.length === 0;
    }
    if (!isEmpty(filter.match(numericOperators))) {
      return operatorFilter(filter, Number(value));
    }
    return false;
  } else {
    return Number(value) === Number(filter.trim());
  }
}

function isNumeric(value: unknown): boolean {
  return (
    (typeof value === 'number' ||
      (typeof value === 'string' && value.trim() !== '')) &&
    !isNaN(Number(value))
  );
}

function operatorFilter(filter: string, value: unknown): boolean {
  // Callers only pass a filter that starts with an operator.
  const operator = (filter.match(numericOperators) as RegExpMatchArray)[0];
  const operand = filter.replace(numericOperators, '').trim();
  // Compare as numbers when both sides are numeric, so "10" > "9". Otherwise
  // compare as text, so ISO date strings still work.
  const numeric = isNumeric(value) && isNumeric(operand);
  // Typed as a string so the comparisons compile; JS coerces as it always
  // has: numerically, unless both sides are strings.
  const actual = (numeric ? Number(value) : value) as string;
  const expected = (numeric ? Number(operand) : operand) as string;
  switch (operator) {
    case '>':
      return actual > expected;
    case '>=':
      return actual >= expected;
    case '<':
      return actual < expected;
    case '<=':
      return actual <= expected;
    default:
      return false;
  }
}

const monthFormat = new Intl.DateTimeFormat('en', { month: 'long' });
const monthNames = Array.from({ length: 12 }, (_, month) =>
  monthFormat.format(new Date(2000, month, 1)).toLowerCase(),
);

// The orders of the day (D), month (M) and year (Y) in a date filter.
// A month first (MDY) must be a name: 5/3/2026 is 5 March, not 3 May.
// A part of YMD must start with the year: 3/5 is 3 May, not 5 March.
const dateOrders = ['DMY', 'YMD', 'MDY'];

// The period that a date comparison names: a day, a month or a year.
// `start` is the first moment of the period. `end` is the first moment after it.
interface DatePeriod {
  start: number;
  end: number;
}

// A table calls filterValue once for each row with the same filter.
// Keep the parsed last filter, so that each row does not parse it again.
let lastDateFilter = '';
let lastDateOperator = '';
let lastDateParts: string[] = [];
let lastDatePeriod: DatePeriod | undefined;

const isYear = (part = '') => /^\d{4}$/.test(part);

// Gives the month from 0 to 11, from a number or a month name.
function parseMonth(part = ''): number | undefined {
  if (/^\d{1,2}$/.test(part)) {
    const month = Number(part) - 1;
    return month >= 0 && month < 12 ? month : undefined;
  }
  const month =
    part.length < 3
      ? -1
      : monthNames.findIndex((name) => name.startsWith(part));
  return month === -1 ? undefined : month;
}

// Reads a full date, a month and year, or a year, in the formats that
// filterDate matches. A comparison needs a 4-digit year.
function parseDatePeriod(parts: string[]): DatePeriod | undefined {
  const [first = '', second, third] = parts;
  let year: number;
  let month: number | undefined;
  let day: string | undefined;
  if (parts.length === 3) {
    if (isYear(first)) {
      [year, month, day] = [Number(first), parseMonth(second), third];
    } else if (/^\d+$/.test(first)) {
      [day, month, year] = [first, parseMonth(second), Number(third)];
    } else {
      [month, day, year] = [parseMonth(first), second, Number(third)];
    }
    if (
      !isYear(String(year)) ||
      month === undefined ||
      !/^\d{1,2}$/.test(day ?? '')
    ) {
      return undefined;
    }
    const start = new Date(year, month, Number(day));
    // A day that does not exist, such as 31/4, moves to the next month.
    if (start.getMonth() !== month) {
      return undefined;
    }
    return {
      start: start.getTime(),
      end: new Date(year, month, Number(day) + 1).getTime(),
    };
  }
  if (parts.length === 2) {
    [year, month] = isYear(first)
      ? [Number(first), parseMonth(second)]
      : [Number(second), parseMonth(first)];
    if (!isYear(String(year)) || month === undefined) {
      return undefined;
    }
    return {
      start: new Date(year, month, 1).getTime(),
      end: new Date(year, month + 1, 1).getTime(),
    };
  }
  if (parts.length === 1 && isYear(first)) {
    year = Number(first);
    return {
      start: new Date(year, 0, 1).getTime(),
      end: new Date(year + 1, 0, 1).getTime(),
    };
  }
  return undefined;
}

function compareDate(
  time: number,
  operator: string,
  period: DatePeriod,
): boolean {
  switch (operator) {
    case '>':
      return time >= period.end;
    case '>=':
      return time >= period.start;
    case '<':
      return time < period.start;
    default:
      // '<='
      return time < period.end;
  }
}

function datePartMatches(
  slot: string,
  part: string,
  date: Date,
  isLast: boolean,
): boolean {
  const isName = !/^\d+$/.test(part);
  const year = date.getFullYear();
  switch (slot) {
    case 'D':
      return !isName && Number(part) === date.getDate();
    case 'M':
      return isName
        ? (monthNames[date.getMonth()] as string).startsWith(part)
        : Number(part) === date.getMonth() + 1;
    default:
      return (
        !isName &&
        (Number(part) === year ||
          (part.length === 2 && Number(part) === year % 100) ||
          // A year that you are still typing.
          (isLast && String(year).startsWith(part)))
      );
  }
}

// filterDate matches a date filter in a common format: 5/3/2026, 05-03-26,
// 2026-03-05, 5 Mar 2026, March 5 2026, or a part such as "Mar 2026" or "5/3".
// With >, >=, < or <=, it compares the date with a day, a month or a year:
// ">Mar 2026" is after March 2026, "<=2025" is up to the end of 2025.
function filterDate(date: Date, filter: string): boolean {
  if (filter !== lastDateFilter) {
    lastDateFilter = filter;
    const trimmed = filter.trim();
    lastDateOperator = trimmed.match(numericOperators)?.[0] ?? '';
    lastDateParts = trimmed
      .slice(lastDateOperator.length)
      .toLowerCase()
      .split(/[\s/.,-]+/)
      .filter(Boolean);
    lastDatePeriod = lastDateOperator
      ? parseDatePeriod(lastDateParts)
      : undefined;
  }
  if (lastDateOperator) {
    // A comparison has no effect until you type a full date, month or year.
    return lastDatePeriod
      ? compareDate(date.getTime(), lastDateOperator, lastDatePeriod)
      : true;
  }
  const parts = lastDateParts;
  if (parts.length === 0) {
    return true;
  }
  if (parts.length > 3) {
    return false;
  }
  return dateOrders.some((order) => {
    const lastStart = order === 'YMD' ? 0 : order.length - parts.length;
    for (let start = 0; start <= lastStart; start++) {
      const matches = parts.every((part, index) => {
        const slot = order[start + index] as string;
        if (order === 'MDY' && slot === 'M' && /^\d+$/.test(part)) {
          return false;
        }
        return datePartMatches(slot, part, date, index === parts.length - 1);
      });
      if (matches) {
        return true;
      }
    }
    return false;
  });
}

export function filterValue(
  value: FilterableValue,
  datatype: number | undefined,
  filter: string,
  options?: Iterable<FilterOption> | null,
): boolean {
  if (!isEmpty(filter.match(/&&/))) {
    return filter
      .split('&&')
      .every((subFilter) =>
        filterValue(value, datatype, subFilter.trim(), options),
      );
  }
  if (!isEmpty(filter.match(/\|\|/))) {
    return filter
      .split('||')
      .some((subFilter) =>
        filterValue(value, datatype, subFilter.trim(), options),
      );
  }

  let include = true;
  let negated = false;

  if (filter.charAt(0) === '!') {
    // Negated filter - flag and remove the leading "!"
    negated = true;
    filter = filter.substring(1).trim();
  }

  if (value instanceof Date) {
    const include = filter === '*' || filterDate(value, filter);
    return negated ? !include : include;
  }

  const rawValue = value;

  if (datatype !== DataType.LIST && value) {
    value = stripDiacritics(value as string | number);
  }
  filter = stripDiacritics(filter);

  switch (datatype) {
    case DataType.STRING:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        // A truthy value was stripped to a string above.
        const valueLowerCase = value ? (value as string).toLowerCase() : '';
        const filterLowerCase = filter.toLowerCase();
        if (isEmpty(options)) {
          if (filter.match(numericOperators)) {
            return operatorFilter(filter.trim(), value);
          }
          if (valueLowerCase.indexOf(filterLowerCase) === -1) {
            include = false;
          }
        } else {
          const option = findBy(
            options as Iterable<FilterOption>,
            'value',
            rawValue,
          );
          if (!option) {
            // Not a valid option - ignore it / consider it empty.
            include = false;
          } else {
            const labelLowerCase = stripDiacritics(option.label.toLowerCase());
            const matchesValue = valueLowerCase.indexOf(filterLowerCase) > -1;
            const matchesLabel = labelLowerCase.indexOf(filterLowerCase) > -1;
            include = matchesValue || matchesLabel;
          }
        }
      }
      break;
    case DataType.BOOLEAN:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        include = isTruthy(filter) === (value === 'true');
      }
      break;
    case DataType.INTEGER:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        if (isEmpty(options)) {
          include = isPresent(value) && filterNumeric(filter, value);
        } else {
          // parseInt() stringifies its argument itself.
          value = parseInt(value as string, 10);
          const filterLowerCase = filter.toLowerCase();
          const option = findBy(
            options as Iterable<FilterOption>,
            'valueInt',
            value,
          );

          if (!option) {
            // Not a valid option - ignore it / consider it empty.
            include = false;
          } else {
            const labelLowerCase = stripDiacritics(option.label.toLowerCase());
            const matchesValue =
              isPresent(value) && filterNumeric(filter, value);
            const matchesLabel = labelLowerCase.indexOf(filterLowerCase) > -1;
            include = matchesValue || matchesLabel;
          }
        }
      }
      break;
    case DataType.FLOAT:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        include = isPresent(value) && filterNumeric(filter, value);
      }
      break;
    case DataType.LIST:
      include = false;
      // A plain, Ember or fragment array. Other values (for example the ''
      // fallback) have no items.
      if (typeof value !== 'object' || value === null) {
        break;
      }
      for (const el of value as Iterable<string>) {
        if (filter === '*') {
          include = isPresent(el);
        } else {
          const option = findBy(options ?? [], 'value', el);
          if (!option) {
            // Not a valid option - ignore it / consider it empty.
            continue;
          }

          const value = stripDiacritics(el.toLowerCase());
          const label = stripDiacritics(option.label.toLowerCase());
          const filterLower = filter.toLowerCase();
          const matchesValue = value.indexOf(filterLower) > -1;
          const matchesLabel = label.indexOf(filterLower) > -1;
          include = matchesValue || matchesLabel;
        }
        if (include) {
          break;
        }
      }
      break;
    case undefined: // No value for this field
      include = false;
      break;
  }

  if (negated) {
    include = !include;
  }

  return include;
}

// A list filter row, as the people, sickbay and wellbeing lists build them:
// the field key and the user's filter expression for it.
export interface FieldFilter {
  key: string;
  value: string;
  dataType?: number;
}

// getNumericYearLevelFilter takes the year level filter row (if any) and the
// school's year level options (if applicable), returning a filter string with
// year levels replaced with their underlying numeric values (for schools which
// use a select list).
export function getNumericYearLevelFilter(
  filter: FieldFilter | undefined,
  yearLevelOptions?: readonly FieldOption[] | null,
): string {
  // An empty value used to fall back to returning the filter object itself.
  let yearLevelFilterValue = filter?.value;
  if (!yearLevelFilterValue) {
    return '';
  }
  if (yearLevelOptions && !isEmpty(yearLevelOptions)) {
    // This school uses a select for year levels.
    // Map labels to their corresponding values for filtering.
    yearLevelFilterValue = yearLevelOptions.reduce((filterValue, option) => {
      const exp = new RegExp(`\\b${option.label}\\b`, 'gi');
      return filterValue.replaceAll(exp, `${option.valueInt}`);
    }, yearLevelFilterValue);
  }
  return yearLevelFilterValue;
}
